import React, { useEffect, useState } from 'react';
import { klona } from 'klona';
import { Icon } from '@fuxi/eevee-icon';
import arrowImg from '@/assets/image/arrow-gradient.png';
import { ShowTitle } from '../show-title';
import {
  setCurrentStep,
  setFormValue,
  setNextBtnConfig,
  setPrevBtnConfig,
  TFormValue,
  useAIDiagnoseStore,
} from '../../store/useAIDiagnoseStore';
import { BodyDimensionSelector } from '../body-dimension-selector';
import { PART_2_LABEL_TEXT } from '../current-dimension';

import cx from './index.module.scss';

interface Props {}

export const TargetDimension: React.FC<Props> = () => {
  const { selectPart } = useAIDiagnoseStore.use.formValue();
  const [isPickerShow, setIsPickerShow] = useState(false);
  const { currentDimension, targetDimension } = useAIDiagnoseStore.use.formValue();
  const [currentPickerFields, setCurrentPickerFields] = useState<(keyof TFormValue['targetDimension'])[]>([]);

  useEffect(() => {
    setPrevBtnConfig({
      show: true,
      action: () => {
        setCurrentStep(prev => ({ currentStep: prev - 1 }));
      },
    });

    // 应该填写的表单key值
    const shouldFillFieldsKey = selectPart
      ?.map(item => PART_2_LABEL_TEXT[item]?.partFields?.concat(PART_2_LABEL_TEXT[item]?.extraFields || []))
      .flat();

    setNextBtnConfig({
      disable: shouldFillFieldsKey?.some(item => !targetDimension[item]),
      action: () => {
        setCurrentStep(prev => ({ currentStep: prev + 1 }));
      },
    });
  }, [targetDimension, selectPart]);

  return (
    <div className={cx('TargetDimension')}>
      <ShowTitle title="您的目标围度" subTitle="明确目标，我们一起努力" />
      <div className={cx('dimension-tip')}>
        <div className={cx('dimension-tip-item')}>当前围度</div>
        <div className={cx('dimension-tip-item')}>目标围度</div>
      </div>
      <div className={cx('dimension-form')}>
        {klona(selectPart)
          // 表单排序
          ?.sort((a, b) => Object.keys(PART_2_LABEL_TEXT)?.indexOf(a) - Object.keys(PART_2_LABEL_TEXT)?.indexOf(b))
          ?.map(item => (
            <div
              className={cx('part-form-item', {
                'no-value': !targetDimension[PART_2_LABEL_TEXT[item]?.partFields[0]],
              })}
              key={item}
            >
              <div className={cx('label')}>{PART_2_LABEL_TEXT[item]?.name}</div>
              <div className={cx('input-box')}>
                {['waistCircumference', 'backThickness'].includes(item) ? (
                  <>
                    <div className={cx('left')}>
                      <div className={cx('input-item')}>
                        <span className={cx('value-text')}>
                          <>
                            <span>{currentDimension[item]}</span>
                            <span className={cx('suffix')}>厘米</span>
                          </>
                        </span>
                      </div>
                    </div>
                    <div className={cx('arrow-box')}>
                      <img src={arrowImg} alt="" />
                    </div>
                    <div
                      className={cx('right')}
                      onClick={() => {
                        setCurrentPickerFields(PART_2_LABEL_TEXT[item]?.partFields);
                        setIsPickerShow(true);
                      }}
                    >
                      <div className={cx('input-item')}>
                        <span className={cx('value-text')}>
                          {targetDimension[item] ? (
                            <>
                              <span>{targetDimension[item]}</span>
                              <span className={cx('suffix')}>厘米</span>
                            </>
                          ) : (
                            <span>请录入{PART_2_LABEL_TEXT[item]?.name}</span>
                          )}
                        </span>
                        <Icon name="arrow-right" />
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <div className={cx('left')}>
                      <div className={cx('input-item')}>
                        <div className={cx('value-wrapper')}>
                          <span className={cx('value-text')}>
                            <>
                              <span>左：</span>
                              <span>{currentDimension[PART_2_LABEL_TEXT[item]?.partFields[0]]}</span>
                            </>
                          </span>
                          <span className={cx('value-text')}>
                            <>
                              <span>右：</span>
                              <span>{currentDimension[PART_2_LABEL_TEXT[item]?.partFields[1]]}</span>
                              <span className={cx('suffix')}>厘米</span>
                            </>
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className={cx('arrow-box')}>
                      <img src={arrowImg} alt="" />
                    </div>
                    <div
                      className={cx('right')}
                      onClick={() => {
                        setCurrentPickerFields(PART_2_LABEL_TEXT[item]?.partFields);
                        setIsPickerShow(true);
                      }}
                    >
                      <div className={cx('input-item')}>
                        <div className={cx('value-wrapper')}>
                          <span className={cx('value-text')}>
                            {targetDimension[PART_2_LABEL_TEXT[item]?.partFields[0]] ? (
                              <>
                                <span>左：</span>
                                <span>{targetDimension[PART_2_LABEL_TEXT[item]?.partFields[0]]}</span>
                              </>
                            ) : (
                              <span>请录入左{PART_2_LABEL_TEXT[item]?.name}</span>
                            )}
                          </span>
                          <span className={cx('value-text')}>
                            {targetDimension[PART_2_LABEL_TEXT[item]?.partFields[1]] ? (
                              <>
                                <span>右：</span>
                                <span>{targetDimension[PART_2_LABEL_TEXT[item]?.partFields[1]]}</span>
                                <span className={cx('suffix')}>厘米</span>
                              </>
                            ) : (
                              <span>右{PART_2_LABEL_TEXT[item]?.name}</span>
                            )}
                          </span>
                        </div>
                        <Icon name="arrow-right" />
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          ))}
        {selectPart?.includes('thighCircumference') && (
          <div className={cx('part-form-item', { 'no-value': !targetDimension['leftCalfCircumference'] })}>
            <div className={cx('label')}>小腿围</div>
            <div className={cx('input-box')}>
              <div className={cx('left')}>
                <div className={cx('input-item')}>
                  <div className={cx('value-wrapper')}>
                    <span className={cx('value-text')}>
                      <>
                        <span>左：</span>
                        <span>{currentDimension['leftCalfCircumference']}</span>
                      </>
                    </span>
                    <span className={cx('value-text')}>
                      <>
                        <span>右：</span>
                        <span>{currentDimension['rightCalfCircumference']}</span>
                        <span className={cx('suffix')}>厘米</span>
                      </>
                    </span>
                  </div>
                </div>
              </div>
              <div className={cx('arrow-box')}>
                <img src={arrowImg} alt="" />
              </div>
              <div
                className={cx('right')}
                onClick={() => {
                  setCurrentPickerFields(['leftCalfCircumference', 'rightCalfCircumference']);
                  setIsPickerShow(true);
                }}
              >
                <div className={cx('input-item')}>
                  <div className={cx('value-wrapper')}>
                    <span className={cx('value-text')}>
                      {targetDimension['leftCalfCircumference'] ? (
                        <>
                          <span>左：</span>
                          <span>{targetDimension['leftCalfCircumference']}</span>
                        </>
                      ) : (
                        <span>请录入左小腿围</span>
                      )}
                    </span>
                    <span className={cx('value-text')}>
                      {targetDimension['rightCalfCircumference'] ? (
                        <>
                          <span>右：</span>
                          <span>{targetDimension['rightCalfCircumference']}</span>
                          <span className={cx('suffix')}>厘米</span>
                        </>
                      ) : (
                        <span>右小腿围</span>
                      )}
                    </span>
                  </div>
                  <Icon name="arrow-right" />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      <BodyDimensionSelector
        value={targetDimension}
        onChange={value => {
          setFormValue(
            {
              targetDimension: value,
            },
            { isMerge: true }
          );
        }}
        isShow={isPickerShow}
        setIsShow={setIsPickerShow}
        fields={currentPickerFields}
        setFields={setCurrentPickerFields}
      />
    </div>
  );
};
